import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';

interface Checklist {
  id: number;
  veiculo: string;
  motorista: string;
  data_checklist: string;
  tipo: string;
  status: string;
  resultado: string;
  observacoes?: string;
  quilometragem: number;
  itens_verificados: number;
  itens_aprovados: number;
  itens_reprovados: number;
  itens: ChecklistItem[];
  equipamento_tag?: string;
  equipamento_nome?: string;
  equipamento_tipo?: string;
  categoria: 'veiculo' | 'equipamento';
}

interface ChecklistItem {
  id: number;
  item: string;
  status: string;
  observacao?: string;
  categoria: string;
  obrigatorio: boolean;
}

const Checklists: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedChecklist, setExpandedChecklist] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Estados para ordenação e preferências de colunas
  const [sortBy, setSortBy] = useState<string>('data_checklist');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'data_checklist', 'veiculo', 'motorista', 'tipo', 'resultado', 'status'
  ]); // quilometragem oculta por padrão
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Estados para redimensionamento de colunas
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Flag para controlar primeira carga (não salvar durante carregamento inicial)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook de infinite scroll
  const {
    items: checklists,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<Checklist>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getChecklists({
          page,
          limit,
          orderBy: 'data_realizacao',
          order: 'desc'
        });

        if (response.success && response.data) {
          const apiChecklists = Array.isArray(response.data.checklists) ? response.data.checklists : [];

          const convertedChecklists: Checklist[] = apiChecklists.map((checklist: any) => {
            const itensVerificados = checklist.itens?.length || 0;
            const itensAprovados = checklist.itens?.filter((item: any) => item.valor === 'aprovado' || item.valor === 'conforme').length || 0;
            const itensReprovados = checklist.itens?.filter((item: any) => item.valor === 'reprovado' || item.valor === 'nao_conforme').length || 0;
            const resultadoPercentual = itensVerificados > 0 ? Math.round((itensAprovados / itensVerificados) * 100) : 0;
            const isEquipamento = !!checklist.equipamento_id;

            return {
              id: checklist.id,
              veiculo: checklist.veiculo_placa || 'N/A',
              motorista: checklist.motorista_nome || checklist.usuario_nome || 'N/A',
              data_checklist: checklist.data_realizacao,
              tipo: checklist.tipo === 'pre_viagem' ? 'Pré-viagem' :
                    checklist.tipo === 'pos_viagem' ? 'Pós-viagem' :
                    checklist.tipo === 'diario' ? 'Diário' : 'Periódico',
              status: checklist.status === 'concluido' ? 'Concluído' :
                      checklist.status === 'reprovado' ? 'Reprovado' : 'Pendente',
              resultado: `${resultadoPercentual}%`,
              observacoes: checklist.observacoes,
              quilometragem: checklist.odometro,
              itens_verificados: itensVerificados,
              itens_aprovados: itensAprovados,
              itens_reprovados: itensReprovados,
              equipamento_tag: checklist.equipamento_tag,
              equipamento_nome: checklist.equipamento_nome,
              equipamento_tipo: checklist.equipamento_tipo,
              categoria: isEquipamento ? 'equipamento' : 'veiculo',
              itens: (checklist.itens || []).map((item: any) => ({
                id: item.id,
                item: item.descricao,
                status: (item.valor === 'aprovado' || item.valor === 'conforme') ? 'Aprovado' :
                       (item.valor === 'reprovado' || item.valor === 'nao_conforme') ? 'Reprovado' : 'Pendente',
                observacao: item.observacao,
                categoria: item.categoria,
                obrigatorio: item.obrigatorio
              }))
            };
          });

          return {
            success: true,
            data: {
              items: convertedChecklists,
              pagination: {
                total: (response.data as any).total || (response.data as any).pagination?.total || convertedChecklists.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar checklists:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar checklists'
        };
      }
    },
    itemsPerPage: 10,
    filters: {},
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Concluído': 'status-success',
      'Aprovado': 'status-success',
      'Reprovado': 'status-danger',
      'Pendente': 'status-warning'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getItemStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Aprovado': 'status-success',
      'Reprovado': 'status-danger',
      'Pendente': 'status-warning'
    };
    return statusMap[status] || 'status-secondary';
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const toggleExpand = (id: number) => {
    setExpandedChecklist(expandedChecklist === id ? null : id);
  };

  // Carregar preferências ao montar
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await apiService.getUserTablePreferences('checklists');
        if (response.success && response.data?.preferences) {
          const prefs = response.data.preferences;
          if (prefs.visibleColumns) setVisibleColumns(prefs.visibleColumns);
          if (prefs.columnWidths) setColumnWidths(prefs.columnWidths);
          if (prefs.sortBy) setSortBy(prefs.sortBy);
          if (prefs.sortOrder) setSortOrder(prefs.sortOrder);
        }
      } catch (error) {
        console.error('Erro ao carregar preferências:', error);
      } finally {
        // Marca que terminou de carregar (agora pode salvar alterações)
        setIsInitialLoad(false);
      }
    };
    loadPreferences();
  }, []);

  // Salvar preferências com debounce
  const savePreferences = useCallback(async () => {
    // Não salvar durante carregamento inicial
    if (isInitialLoad) {
      return;
    }

    try {
      await apiService.saveUserTablePreferences('checklists', {
        visibleColumns,
        columnWidths,
        sortBy,
        sortOrder
      });
    } catch (error) {
      // Silenciosamente falhar para não atrapalhar a experiência do usuário
    }
  }, [isInitialLoad, visibleColumns, columnWidths, sortBy, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      savePreferences();
    }, 500);
    return () => clearTimeout(timer);
  }, [savePreferences]);

  // Fechar seletor de colunas ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      if (showColumnSelector) {
        setShowColumnSelector(false);
      }
    };
    if (showColumnSelector) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showColumnSelector]);

  // Aplicar filtros e ordenação
  const filteredAndSortedChecklists = useMemo(() => {
    let result = checklists.filter(checklist => {
      const matchesSearch = (checklist.veiculo && checklist.veiculo.toLowerCase().includes(filter.toLowerCase())) ||
                           (checklist.motorista && checklist.motorista.toLowerCase().includes(filter.toLowerCase())) ||
                           (checklist.tipo && checklist.tipo.toLowerCase().includes(filter.toLowerCase()));
      const matchesStatus = !statusFilter || checklist.status === statusFilter;
      const matchesType = !typeFilter || checklist.tipo === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });

    // Aplicar ordenação
    if (sortBy) {
      result = [...result].sort((a, b) => {
        let aValue = a[sortBy as keyof Checklist];
        let bValue = b[sortBy as keyof Checklist];

        // Tratamento especial para datas
        if (sortBy === 'data_checklist') {
          aValue = new Date(a.data_checklist).getTime();
          bValue = new Date(b.data_checklist).getTime();
        }

        // Tratamento para números
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Tratamento para strings
        const aStr = String(aValue || '').toLowerCase();
        const bStr = String(bValue || '').toLowerCase();

        if (sortOrder === 'asc') {
          return aStr.localeCompare(bStr, 'pt-BR');
        } else {
          return bStr.localeCompare(aStr, 'pt-BR');
        }
      });
    }

    return result;
  }, [checklists, filter, statusFilter, typeFilter, sortBy, sortOrder]);

  const viewChecklist = (id: number) => {
    navigate(`/checklists/visualizar/${id}`);
  };

  const editChecklist = (id: number) => {
    navigate(`/checklists/editar/${id}`);
  };

  // Ordenar por coluna
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Toggle visibilidade de coluna
  const toggleColumnVisibility = (column: string) => {
    if (visibleColumns.includes(column)) {
      setVisibleColumns(visibleColumns.filter(c => c !== column));
    } else {
      setVisibleColumns([...visibleColumns, column]);
    }
  };

  // Iniciar redimensionamento de coluna
  const handleResizeStart = (column: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest('th');
    if (th) {
      setResizingColumn(column);
      setResizeStartX(e.clientX);
      setResizeStartWidth(th.offsetWidth);
    }
  };

  // Redimensionar coluna durante movimento do mouse
  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX;
      const newWidth = Math.max(80, resizeStartWidth + delta); // Mínimo 80px

      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: newWidth
      }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  // Obter largura da coluna
  const getColumnWidth = (columnKey: string): number | undefined => {
    return columnWidths[columnKey];
  };

  // Colunas disponíveis
  const availableColumns = [
    { key: 'data_checklist', label: 'Data/Hora' },
    { key: 'veiculo', label: 'Veículo' },
    { key: 'motorista', label: 'Motorista' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'quilometragem', label: 'Quilometragem' },
    { key: 'resultado', label: 'Resultado' },
    { key: 'status', label: 'Status' }
  ];

  const deleteChecklist = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este checklist?')) {
      try {
        // Fazer chamada real para o backend
        const response = await apiService.deleteChecklist(id);

        if (response.success) {
          utils.showNotification('Checklist excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erro ao excluir checklist');
        }
      } catch (error) {
        console.error('Erro ao excluir checklist:', error);
        utils.showNotification('Erro ao excluir checklist', { type: 'error' });
      }
    }
  };

  const getTypes = () => {
    const types = Array.from(new Set(checklists.map(c => c.tipo)));
    return types;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(checklists.map(c => c.status)));
    return statuses;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Checklists</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Novo Checklist
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando checklists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Checklists</h1>
        {canCreate(SystemModule.CHECKLISTS) && (
          <button
            className="btn btn-primary"
            onClick={() => navigate('/checklists/novo')}
          >
            <i className="fas fa-plus"></i>
            Novo Checklist
          </button>
        )}
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Histórico de Checklists</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* Botão de Seletor de Colunas */}
              <div style={{ position: 'relative' }}>
                <button
                  className="btn btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowColumnSelector(!showColumnSelector);
                  }}
                  title="Selecionar colunas"
                >
                  <i className="fas fa-columns"></i>
                </button>
                {showColumnSelector && (
                  <div onClick={(e) => e.stopPropagation()} style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '5px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '200px',
                    padding: '10px'
                  }}>
                    <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                      Colunas Visíveis
                    </div>
                    {availableColumns.map(col => (
                      <label key={col.key} style={{ display: 'block', padding: '5px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.key)}
                          onChange={() => toggleColumnVisibility(col.key)}
                          style={{ marginRight: '8px' }}
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Veículo, motorista ou tipo...'
                },
                {
                  type: 'select',
                  label: 'Tipo',
                  value: typeFilter,
                  onChange: setTypeFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getTypes().map(type => ({ value: type, label: type }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getStatuses().map(status => ({ value: status, label: status }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setStatusFilter('');
                setTypeFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Checklists"
            />
            </div>
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredAndSortedChecklists.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="checklists"
              />
            )}

            {filteredAndSortedChecklists.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-list-check empty-state-icon"></i>
                <h3>Nenhum checklist encontrado</h3>
                <p>{filter || statusFilter || typeFilter ? 'Tente ajustar os filtros de busca.' : 'Realize o primeiro checklist do sistema.'}</p>
                {canCreate(SystemModule.CHECKLISTS) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/checklists/novo')}
                  >
                    Realizar Primeiro Checklist
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{
                        width: '30px',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: '#f8f9fa',
                        boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
                        zIndex: 10
                      }}></th>
                      {visibleColumns.includes('data_checklist') && (
                        <th
                          onClick={() => handleSort('data_checklist')}
                          style={{
                            cursor: 'pointer',
                            position: 'relative',
                            width: getColumnWidth('data_checklist') ? `${getColumnWidth('data_checklist')}px` : undefined
                          }}
                        >
                          Data/Hora
                          {sortBy === 'data_checklist' && (
                            <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>
                          )}
                          <div
                            onMouseDown={(e) => handleResizeStart('data_checklist', e)}
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              backgroundColor: resizingColumn === 'data_checklist' ? '#3498db' : 'transparent',
                              zIndex: 10
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')}
                            onMouseLeave={(e) => {
                              if (resizingColumn !== 'data_checklist') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          />
                        </th>
                      )}
                      {visibleColumns.includes('veiculo') && (
                        <th onClick={() => handleSort('veiculo')} style={{ cursor: 'pointer', position: 'relative', width: getColumnWidth('veiculo') ? `${getColumnWidth('veiculo')}px` : undefined }}>
                          Veículo
                          {sortBy === 'veiculo' && <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>}
                          <div onMouseDown={(e) => handleResizeStart('veiculo', e)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: resizingColumn === 'veiculo' ? '#3498db' : 'transparent', zIndex: 10 }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')} onMouseLeave={(e) => { if (resizingColumn !== 'veiculo') e.currentTarget.style.backgroundColor = 'transparent'; }} />
                        </th>
                      )}
                      {visibleColumns.includes('motorista') && (
                        <th onClick={() => handleSort('motorista')} style={{ cursor: 'pointer', position: 'relative', width: getColumnWidth('motorista') ? `${getColumnWidth('motorista')}px` : undefined }}>
                          Motorista
                          {sortBy === 'motorista' && <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>}
                          <div onMouseDown={(e) => handleResizeStart('motorista', e)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: resizingColumn === 'motorista' ? '#3498db' : 'transparent', zIndex: 10 }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')} onMouseLeave={(e) => { if (resizingColumn !== 'motorista') e.currentTarget.style.backgroundColor = 'transparent'; }} />
                        </th>
                      )}
                      {visibleColumns.includes('tipo') && (
                        <th onClick={() => handleSort('tipo')} style={{ cursor: 'pointer', position: 'relative', width: getColumnWidth('tipo') ? `${getColumnWidth('tipo')}px` : undefined }}>
                          Tipo
                          {sortBy === 'tipo' && <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>}
                          <div onMouseDown={(e) => handleResizeStart('tipo', e)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: resizingColumn === 'tipo' ? '#3498db' : 'transparent', zIndex: 10 }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')} onMouseLeave={(e) => { if (resizingColumn !== 'tipo') e.currentTarget.style.backgroundColor = 'transparent'; }} />
                        </th>
                      )}
                      {visibleColumns.includes('quilometragem') && (
                        <th onClick={() => handleSort('quilometragem')} style={{ cursor: 'pointer', position: 'relative', width: getColumnWidth('quilometragem') ? `${getColumnWidth('quilometragem')}px` : undefined }}>
                          Quilometragem
                          {sortBy === 'quilometragem' && <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>}
                          <div onMouseDown={(e) => handleResizeStart('quilometragem', e)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: resizingColumn === 'quilometragem' ? '#3498db' : 'transparent', zIndex: 10 }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')} onMouseLeave={(e) => { if (resizingColumn !== 'quilometragem') e.currentTarget.style.backgroundColor = 'transparent'; }} />
                        </th>
                      )}
                      {visibleColumns.includes('resultado') && (
                        <th onClick={() => handleSort('resultado')} style={{ cursor: 'pointer', position: 'relative', width: getColumnWidth('resultado') ? `${getColumnWidth('resultado')}px` : undefined }}>
                          Resultado
                          {sortBy === 'resultado' && <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>}
                          <div onMouseDown={(e) => handleResizeStart('resultado', e)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: resizingColumn === 'resultado' ? '#3498db' : 'transparent', zIndex: 10 }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')} onMouseLeave={(e) => { if (resizingColumn !== 'resultado') e.currentTarget.style.backgroundColor = 'transparent'; }} />
                        </th>
                      )}
                      {visibleColumns.includes('status') && (
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', position: 'relative', width: getColumnWidth('status') ? `${getColumnWidth('status')}px` : undefined }}>
                          Status
                          {sortBy === 'status' && <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>}
                          <div onMouseDown={(e) => handleResizeStart('status', e)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: resizingColumn === 'status' ? '#3498db' : 'transparent', zIndex: 10 }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3498db')} onMouseLeave={(e) => { if (resizingColumn !== 'status') e.currentTarget.style.backgroundColor = 'transparent'; }} />
                        </th>
                      )}
                      <th style={{
                        position: 'sticky',
                        right: 0,
                        backgroundColor: '#f8f9fa',
                        boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
                        zIndex: 10
                      }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedChecklists.map(checklist => (
                      <React.Fragment key={checklist.id}>
                        <tr>
                          <td style={{
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                            boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
                            zIndex: 9
                          }}>
                            <button
                              className="expand-btn"
                              onClick={() => toggleExpand(checklist.id)}
                              title={expandedChecklist === checklist.id ? 'Recolher' : 'Expandir'}
                            >
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </td>
                          {visibleColumns.includes('data_checklist') && (
                            <td>
                              <div style={{ fontSize: '0.8rem' }}>
                                {formatDate(checklist.data_checklist)}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#7f8c8d' }}>
                                {formatDateTime(checklist.data_checklist).split(', ')[1]}
                              </div>
                            </td>
                          )}
                          {visibleColumns.includes('veiculo') && (
                            <td>
                              <strong>{checklist.veiculo}</strong>
                            </td>
                          )}
                          {visibleColumns.includes('motorista') && (
                            <td>{checklist.motorista}</td>
                          )}
                          {visibleColumns.includes('tipo') && (
                            <td>
                              <span className="status-badge status-info">
                                {checklist.tipo}
                              </span>
                            </td>
                          )}
                          {visibleColumns.includes('quilometragem') && (
                            <td>
                              {checklist.quilometragem ? `${checklist.quilometragem.toLocaleString('pt-BR')} km` : '-'}
                            </td>
                          )}
                          {visibleColumns.includes('resultado') && (
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  color: checklist.resultado === '100%' ? '#04C64F' : '#e74c3c'
                                }}>
                                  {checklist.resultado}
                                </span>
                                <small style={{ color: '#7f8c8d' }}>
                                  ({checklist.itens_aprovados}/{checklist.itens_verificados})
                                </small>
                              </div>
                            </td>
                          )}
                          {visibleColumns.includes('status') && (
                            <td>
                              <span className={`status-badge ${getStatusClass(checklist.status)}`}>
                                {checklist.status}
                              </span>
                            </td>
                          )}
                          <td style={{
                            position: 'sticky',
                            right: 0,
                            backgroundColor: 'white',
                            boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
                            zIndex: 9
                          }}>
                            <div className="action-buttons">
                              <button
                                className="btn-view"
                                onClick={() => viewChecklist(checklist.id)}
                                title="Visualizar"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              {canUpdate(SystemModule.CHECKLISTS) && (
                                <button
                                  className="btn-edit"
                                  onClick={() => editChecklist(checklist.id)}
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              )}
                              {canDelete(SystemModule.CHECKLISTS) && (
                                <button
                                  className="btn-delete"
                                  onClick={() => deleteChecklist(checklist.id)}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedChecklist === checklist.id && (
                          <tr>
                            <td colSpan={visibleColumns.length + 2}>
                              <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                                  <div>
                                    <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>Detalhes do Checklist</h4>
                                    {checklist.observacoes && (
                                      <div style={{ marginBottom: '15px', padding: '10px', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                                        <strong>Observações:</strong> {checklist.observacoes}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                                      Itens verificados: <strong>{checklist.itens_verificados}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#04C64F' }}>
                                      Aprovados: <strong>{checklist.itens_aprovados}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#e74c3c' }}>
                                      Reprovados: <strong>{checklist.itens_reprovados}</strong>
                                    </div>
                                  </div>
                                </div>

                                {checklist.itens && checklist.itens.length > 0 && (
                                  <>
                                    <h5 style={{ marginBottom: '10px', color: '#2c3e50' }}>Itens Verificados</h5>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
                                      {checklist.itens.map(item => (
                                    <div key={item.id} style={{ 
                                      padding: '10px', 
                                      background: '#fff', 
                                      borderRadius: '6px', 
                                      border: '1px solid #e9ecef',
                                      borderLeft: `4px solid ${item.status === 'Aprovado' ? '#04C64F' : item.status === 'Reprovado' ? '#e74c3c' : '#f39c12'}`
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                        <span style={{ fontWeight: '600' }}>{item.item}</span>
                                        <span className={`status-badge ${getItemStatusClass(item.status)}`}>
                                          {item.status}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                                        <div>Categoria: {item.categoria}</div>
                                        {item.obrigatorio && (
                                          <div style={{ color: '#e74c3c' }}>Obrigatório</div>
                                        )}
                                        {item.observacao && (
                                          <div style={{ marginTop: '5px', color: item.status === 'Reprovado' ? '#e74c3c' : '#7f8c8d' }}>
                                            <strong>Observação:</strong> {item.observacao}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {/* Botão "Ver Mais" para carregamento manual */}
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas dos checklists */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-list-check"></i>
            </div>
            <div className="stat-value">
              {checklists.length}
            </div>
            <div className="stat-label">Total de Checklists</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {checklists.filter(c => c.status === 'Concluído').length}
            </div>
            <div className="stat-label">Concluídos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-times-circle"></i>
            </div>
            <div className="stat-value">
              {checklists.filter(c => c.status === 'Reprovado').length}
            </div>
            <div className="stat-label">Reprovados</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-percentage"></i>
            </div>
            <div className="stat-value">
              {checklists.length > 0
                ? Math.round((checklists.filter(c => c.status === 'Concluído').length / checklists.length) * 100)
                : 0}%
            </div>
            <div className="stat-label">Taxa de Aprovação</div>
          </div>
        </div>

        {/* Distribuição por tipo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Tipo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {getTypes().map(type => {
                const typeChecklists = checklists.filter(c => c.tipo === type);
                const completed = typeChecklists.filter(c => c.status === 'Concluído').length;
                const percentage = Math.round((typeChecklists.length / checklists.length) * 100);

                // Separar por categoria (veículo ou equipamento)
                const veiculosChecklists = typeChecklists.filter(c => c.categoria === 'veiculo');
                const equipamentosChecklists = typeChecklists.filter(c => c.categoria === 'equipamento');

                const veiculosConcluidos = veiculosChecklists.filter(c => c.status === 'Concluído').length;
                const veiculosReprovados = veiculosChecklists.filter(c => c.status === 'Reprovado').length;
                const veiculosPendentes = veiculosChecklists.filter(c => c.status === 'Pendente').length;

                const equipamentosConcluidos = equipamentosChecklists.filter(c => c.status === 'Concluído').length;
                const equipamentosReprovados = equipamentosChecklists.filter(c => c.status === 'Reprovado').length;
                const equipamentosPendentes = equipamentosChecklists.filter(c => c.status === 'Pendente').length;

                return (
                  <div key={type} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{type}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{typeChecklists.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px', marginBottom: '10px' }}>
                      <div
                        style={{
                          background: 'linear-gradient(135deg, #04C64F, #03a542)',
                          height: '100%',
                          borderRadius: '4px',
                          width: `${percentage}%`
                        }}
                      ></div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginBottom: '10px' }}>
                      <div>Concluídos: <strong>{completed}</strong> ({Math.round((completed / typeChecklists.length) * 100)}%)</div>
                      <div>Reprovados: <strong>{typeChecklists.length - completed}</strong></div>
                      <div>Taxa de sucesso: <strong>{Math.round((completed / typeChecklists.length) * 100)}%</strong></div>
                    </div>

                    {/* Separação por Veículos e Equipamentos */}
                    <div style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #dee2e6'
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#495057', marginBottom: '8px' }}>
                        Distribuição por Categoria:
                      </div>

                      {/* Veículos */}
                      {veiculosChecklists.length > 0 && (
                        <div style={{
                          marginBottom: '8px',
                          padding: '8px',
                          background: '#fff',
                          borderRadius: '4px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <i className="fas fa-truck" style={{ color: '#3498db', fontSize: '0.75rem' }}></i>
                              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#2c3e50' }}>
                                Veículos
                              </span>
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#3498db' }}>
                              {veiculosChecklists.length}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#7f8c8d', lineHeight: '1.4' }}>
                            <div>✓ Concluídos: <strong style={{ color: '#04C64F' }}>{veiculosConcluidos}</strong></div>
                            <div>✗ Reprovados: <strong style={{ color: '#e74c3c' }}>{veiculosReprovados}</strong></div>
                            <div>⧗ Pendentes: <strong style={{ color: '#f39c12' }}>{veiculosPendentes}</strong></div>
                          </div>
                        </div>
                      )}

                      {/* Equipamentos */}
                      {equipamentosChecklists.length > 0 && (
                        <div style={{
                          padding: '8px',
                          background: '#fff',
                          borderRadius: '4px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <i className="fas fa-box" style={{ color: '#9b59b6', fontSize: '0.75rem' }}></i>
                              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#2c3e50' }}>
                                Equipamentos
                              </span>
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#9b59b6' }}>
                              {equipamentosChecklists.length}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#7f8c8d', lineHeight: '1.4' }}>
                            <div>✓ Concluídos: <strong style={{ color: '#04C64F' }}>{equipamentosConcluidos}</strong></div>
                            <div>✗ Reprovados: <strong style={{ color: '#e74c3c' }}>{equipamentosReprovados}</strong></div>
                            <div>⧗ Pendentes: <strong style={{ color: '#f39c12' }}>{equipamentosPendentes}</strong></div>
                          </div>
                        </div>
                      )}

                      {veiculosChecklists.length === 0 && equipamentosChecklists.length === 0 && (
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6', fontStyle: 'italic' }}>
                          Sem dados de categorização
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checklists;
